/**
 * @file The analytics report contract: a definition at `analyticsReports/{reportId}` naming its
 * queries, level, period, cadence and recipients, and one run per recipient and scheduled instant at
 * `analyticsReports/{reportId}/runs/{runId}`. Definitions are read and written by `metricsAdmin`;
 * runs are created only by the analytics serving service's `/cycle` route and delivered by the
 * `onAnalyticsReportRunCreated` trigger in `functions/documents`. A run stores its rows, so reopening
 * it never re-queries. `nextRunAt`, `scheduledAt`, `createdAt` and the delivery times are
 * `Date#toISOString()` strings; a `null` `nextRunAt` is never due. Distinct from `HomePageConfig.reports`.
 * The `downloadAnalyticsReportRunPdf` and `emailAnalyticsReportRun` callables serve a stored run on
 * demand, each under a per-user cooldown at `users/{uid}/analyticsCooldowns/reportRuns`.
 * `AnalyticsReportQueryId` names a query a new definition may hold, from `ANALYTICS_REPORT_QUERY_IDS`;
 * a stored run keeps `NamedQueryId`, since it may predate a query leaving that list. A completed run
 * holds its frozen `names` and the scope labels' `teamNoun` when one is set; a run that predates them
 * holds neither, and its readers show ids.
 */
import type { ANALYTICS_REPORT_QUERY_IDS } from './ANALYTICS_REPORT_QUERY_IDS';
import type {
  AnalyticsPeriod,
  AnalyticsQueryProvenance,
  AnalyticsRefusalCode,
  AnalyticsScopeLevel,
  NamedQueryCell,
  NamedQueryId,
  NamedQueryParameters,
  NamedQueryRefusedColumn,
} from './Analytics.NamedQuery.types';

export const ANALYTICS_REPORTS_COLLECTION = 'analyticsReports';

/** Reserved for analytics report runs: the `{path=**}/runs/{runId}` collection-group rule in `firestore.rules` lets a recipient read any document named by this collection id at any depth, so no other collection may be called `runs`. */
export const ANALYTICS_REPORT_RUNS_COLLECTION = 'runs';

export const ANALYTICS_REPORT_RUN_DOCUMENT = `${ANALYTICS_REPORTS_COLLECTION}/{reportId}/${ANALYTICS_REPORT_RUNS_COLLECTION}/{runId}`;

export const ANALYTICS_REPORT_MAX_ROWS = 500;

/** The closing line of a report run in the React view and the PDF; the run's provenance goes beside it (a tooltip in React). */
export const ANALYTICS_REPORT_RUN_FOOTER = 'Generated by Syncretic Flows — frozen snapshot. Powered by Syncretic';

export const ANALYTICS_REPORT_CADENCES = ['day', 'week', 'month'] as const;

export const analyticsReportPath = (reportId: string): string => `${ANALYTICS_REPORTS_COLLECTION}/${reportId}`;

export const analyticsReportRunPath = (reportId: string, runId: string): string =>
  `${analyticsReportPath(reportId)}/${ANALYTICS_REPORT_RUNS_COLLECTION}/${runId}`;

/** A recipient's run is `{scheduledAt}_{uid}`; a malformed definition's single failed run is `{scheduledAt}`. */
export const analyticsReportRunId = (scheduledAt: string, recipientUid?: string): string =>
  recipientUid === undefined ? scheduledAt : `${scheduledAt}_${recipientUid}`;

export type AnalyticsReportQueryId = (typeof ANALYTICS_REPORT_QUERY_IDS)[number];

export type AnalyticsScopeRefusalReason = 'forbiddenLevel' | 'noMetricsRight' | 'disabled' | 'noTeam';

/** A week runs on the calendar's `weekStartsOn`, a month on the 1st, at `hour` local time. */
export type AnalyticsReportCadence = { every: (typeof ANALYTICS_REPORT_CADENCES)[number]; hour: number };

/** The definition's `period` is added to `parameters` when the query runs. */
export type AnalyticsReportQuery = { query: NamedQueryId; parameters: Omit<NamedQueryParameters, 'period'> };

export type AnalyticsReportDefinition = {
  name: string;
  queries: AnalyticsReportQuery[];
  level: AnalyticsScopeLevel;
  period: AnalyticsPeriod;
  cadence: AnalyticsReportCadence;
  recipientUids: string[];
  nextRunAt: string | null;
};

/** For a refused entry, `key` is the missing binding or the outdated fact table. */
export type AnalyticsReportQueryRun =
  | {
      query: NamedQueryId;
      status: 'answered';
      columns: string[];
      rows: Record<string, NamedQueryCell>[];
      truncated: boolean;
      refusedColumns: NamedQueryRefusedColumn[];
      provenance: AnalyticsQueryProvenance;
    }
  | { query: NamedQueryId; status: 'refused'; refusal: Extract<AnalyticsRefusalCode, 'notConfigured' | 'factTableOutdated'>; key: string };

/** The names frozen into a run for the person and team ids its rows hold, read when the run is computed; an id with no name is absent. `teams` stays empty for a recipient who may not read Teams records. */
export type AnalyticsReportRunNames = { people: Record<string, string>; teams: Record<string, string> };

/** `deliveryClaimedAt` is set once before sending, so a run is emailed at most once. `deliveryError` is the failure's error name, never its message, because the recipient can read the run. */
export type AnalyticsReportRunDelivery = { deliveryClaimedAt?: string; deliveredAt?: string; deliveryError?: string };

export type AnalyticsReportRun =
  | ({
      status: 'completed';
      reportId: string;
      name: string;
      scheduledAt: string;
      createdAt: string;
      recipientUid: string;
      level: AnalyticsScopeLevel;
      period: AnalyticsPeriod;
      snapshotId: number;
      queries: AnalyticsReportQueryRun[];
      names?: AnalyticsReportRunNames;
      teamNoun?: string;
    } & AnalyticsReportRunDelivery)
  | {
      status: 'refused';
      reportId: string;
      name: string;
      scheduledAt: string;
      createdAt: string;
      recipientUid: string;
      level: AnalyticsScopeLevel;
      refusal: Extract<AnalyticsRefusalCode, 'forbiddenLevel' | 'noTeam'>;
      reason: AnalyticsScopeRefusalReason;
    }
  | { status: 'failed'; reportId: string; name: string | null; scheduledAt: string; createdAt: string; recipientUid: null; reason: string };

/** A run that holds rows to deliver; the only kind the delivery trigger emails. */
export type CompletedAnalyticsReportRun = Extract<AnalyticsReportRun, { status: 'completed' }>;

export const ANALYTICS_COOLDOWNS_COLLECTION = 'analyticsCooldowns';

export const ANALYTICS_REPORT_RUN_COOLDOWN_ID = 'reportRuns';

export const analyticsReportRunCooldownPath = (uid: string): string =>
  `users/${uid}/${ANALYTICS_COOLDOWNS_COLLECTION}/${ANALYTICS_REPORT_RUN_COOLDOWN_ID}`;

/** Per user: one PDF download per 30 s, one Email now per 5 minutes. */
export const ANALYTICS_REPORT_RUN_COOLDOWN_SECONDS = { download: 30, email: 300 } as const;

export type AnalyticsReportRunCooldownKind = keyof typeof ANALYTICS_REPORT_RUN_COOLDOWN_SECONDS;

/** The instant each callable last claimed its cooldown; written only by the documents codebase's admin SDK. */
export type AnalyticsReportRunCooldown = { downloadClaimedAt?: string; emailClaimedAt?: string };

export type AnalyticsReportRunRequest = { reportId: string; runId: string };

/** The details of a `resource-exhausted` refusal and of a failure after the claim. */
export type AnalyticsReportRunCooldownDetails = { retryAt: string };

export type DownloadAnalyticsReportRunPdfResponse = { pdf: string; contentType: 'application/pdf'; filename: string; retryAt: string };

export type EmailAnalyticsReportRunResponse = { retryAt: string };
